import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEventsOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';

import {
  AdminSweepstakesRow,
  adminSweepstakesApi,
  CreateSweepstakesPayload,
} from '../../../../api/adminSweepstakesApi';
import { useCurrentUser } from '../../../../auth/useCurrentUser';
import { paths } from '../../../../routes/paths';
import {
  colors,
  primaryButtonSx,
  secondaryButtonSx,
  statusChipColors,
  tableCellSx,
  tableHeadCellSx,
  typo,
} from '../../../../theme/tokens';

import PublishConfirmationDialog, {
  PublishEntity,
  toPublishEntityFromSweepstakes,
} from '../tasks/PublishConfirmationDialog';
import DependencyImpactDialog from '../tasks/DependencyImpactDialog';
import { WriteButton } from '../../../../components/ui';

function statusChipProps(status: AdminSweepstakesRow['status']): {
  label: string;
  bg: string;
  color: string;
} {
  switch (status) {
    case 'unpublished':
      return { label: 'Unpublished', bg: colors.warningBgStrong, color: colors.warningText };
    case 'in_sync':
      return { label: 'In sync', bg: colors.successBgStrong, color: colors.successText };
    case 'never_published':
    default:
      return { label: 'Never published', bg: colors.bgMuted, color: colors.textSecondary };
  }
}

const STATUS_ORDER: Record<AdminSweepstakesRow['status'], number> = {
  unpublished: 0,
  in_sync: 1,
  never_published: 2,
};

function defaultSort(rows: AdminSweepstakesRow[]): AdminSweepstakesRow[] {
  return [...rows].sort((a, b) => {
    const sa = STATUS_ORDER[a.status] ?? 99;
    const sb = STATUS_ORDER[b.status] ?? 99;
    if (sa !== sb) return sa - sb;
    return (b.updated_at || '').localeCompare(a.updated_at || '');
  });
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffMs = Date.now() - then;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.round(mo / 12);
  return `${yr}y ago`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function initialsFor(email: string | null | undefined): string {
  if (!email) return '?';
  const local = email.split('@')[0] || email;
  const parts = local.split(/[._-]/).filter(Boolean);
  const picks = parts.length >= 2 ? [parts[0][0], parts[1][0]] : [local[0]];
  return picks.join('').toUpperCase().slice(0, 2);
}

type TimeChip = 'today' | '7d' | '30d' | 'all';
const TIME_CHIPS: { key: TimeChip; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: 'all', label: 'All' },
];

function matchesTimeChip(iso: string | null | undefined, chip: TimeChip): boolean {
  if (chip === 'all' || !iso) return chip === 'all' ? true : false;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return false;
  const diffDays = (Date.now() - then) / (1000 * 60 * 60 * 24);
  if (chip === 'today') return diffDays < 1;
  if (chip === '7d') return diffDays < 7;
  if (chip === '30d') return diffDays < 30;
  return true;
}

const EmptyState: React.FC<{ onCreate: () => void }> = ({ onCreate }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: '12px',
      border: `1px solid ${colors.borderLight}`,
      backgroundColor: colors.bgWhite,
      py: 8,
      px: 4,
      textAlign: 'center',
    }}
  >
    <Box
      sx={{
        width: 96,
        height: 96,
        borderRadius: '50%',
        backgroundColor: colors.adminAccentSubtle,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3,
      }}
    >
      <EmojiEventsIcon sx={{ fontSize: 64, color: colors.adminAccent }} />
    </Box>
    <Typography
      variant="h2"
      sx={{ fontSize: 24, fontWeight: 800, fontFamily: 'Manrope, sans-serif', mb: 1 }}
    >
      No sweepstakes yet
    </Typography>
    <Typography sx={{ ...typo.secondaryText, maxWidth: 520, mx: 'auto', mb: 3 }}>
      Platform sweepstakes templates define the entry economics and prize pool tenants can opt
      into.
    </Typography>
    <WriteButton variant="contained" onClick={onCreate} sx={primaryButtonSx} startIcon={<AddIcon />}>
      Create first sweepstake
    </WriteButton>
  </Paper>
);

interface CreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (row: AdminSweepstakesRow) => void;
}

const CreateSweepstakesDialog: React.FC<CreateDialogProps> = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState<CreateSweepstakesPayload>({
    sweepstakes_code: '',
    sweepstakes_name: '',
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({ sweepstakes_code: '', sweepstakes_name: '' });
      setError(null);
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: () => adminSweepstakesApi.create(form),
    onSuccess: (row) => {
      onCreated(row);
      onClose();
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'conflict') setError('A sweepstake with this code already exists.');
      else if (typeof code === 'string') setError(`Save failed: ${code}`);
      else setError('Save failed. Try again.');
    },
  });

  const canSubmit = !!form.sweepstakes_code.trim() && !!form.sweepstakes_name.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
        Create sweepstake
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
          <TextField
            size="small"
            label="sweepstakes_code"
            value={form.sweepstakes_code}
            onChange={(e) => setForm({ ...form, sweepstakes_code: e.target.value })}
            inputProps={{ maxLength: 50 }}
            InputProps={{
              sx: { fontFamily: 'Fira Code, Consolas, monospace', fontSize: 13 },
            }}
            helperText="Lowercase, hyphens. Cannot change after save."
          />
          <TextField
            size="small"
            label="sweepstakes_name"
            value={form.sweepstakes_name}
            onChange={(e) => setForm({ ...form, sweepstakes_name: e.target.value })}
            inputProps={{ maxLength: 120 }}
            helperText={`${form.sweepstakes_name.length} / 120`}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={secondaryButtonSx}>
          Cancel
        </Button>
        <WriteButton
          variant="contained"
          disabled={!canSubmit || createMutation.isPending}
          onClick={() => createMutation.mutate()}
          sx={primaryButtonSx}
        >
          {createMutation.isPending ? 'Creating…' : 'Create'}
        </WriteButton>
      </DialogActions>
    </Dialog>
  );
};

const STATUS_FILTER_OPTIONS: AdminSweepstakesRow['status'][] = [
  'unpublished',
  'in_sync',
  'never_published',
];

const SweepstakesList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSuperAdmin } = useCurrentUser();

  const searchRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<AdminSweepstakesRow['status'][]>([]);
  const [timeChip, setTimeChip] = useState<TimeChip>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [deactivateFor, setDeactivateFor] = useState<AdminSweepstakesRow | null>(null);
  const includeInactive = searchParams.get('includeInactive') === '1';
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error' | 'info';
  }>({ open: false, msg: '', sev: 'success' });

  const pageParam = Number(searchParams.get('page') || '1');
  const pageSizeParam = Number(searchParams.get('pageSize') || '50');
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam - 1 : 0;
  const pageSize = [25, 50, 100, 200].includes(pageSizeParam) ? pageSizeParam : 50;

  const { data: rows = [], isLoading } = useQuery<AdminSweepstakesRow[]>({
    queryKey: ['admin', 'sweepstakes', 'list'],
    queryFn: () => adminSweepstakesApi.list(),
  });

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== '/') return;
      const tag = (ev.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      ev.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && selected.size > 0) setSelected(new Set());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected.size]);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const target = ev.target as HTMLElement | null;
      const tag = target?.tagName;
      const isEditable = target?.isContentEditable;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || isEditable) return;
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
      if (ev.key !== 'p' && ev.key !== 'P') return;
      if (!isSuperAdmin || selected.size === 0 || publishOpen) return;
      ev.preventDefault();
      setPublishOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSuperAdmin, selected.size, publishOpen]);

  const sorted = useMemo(() => defaultSort(rows), [rows]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return sorted.filter((r) => {
      if (!includeInactive && !r.active) return false;
      if (needle) {
        const hay = `${r.sweepstakes_code} ${r.sweepstakes_name ?? ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (statusFilter.length && !statusFilter.includes(r.status)) return false;
      if (!matchesTimeChip(r.updated_at, timeChip)) return false;
      return true;
    });
  }, [sorted, search, statusFilter, timeChip, includeInactive]);

  const paged = useMemo(() => {
    const start = page * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const updateSearchParam = useCallback(
    (next: { page?: number; pageSize?: number }) => {
      const nsp = new URLSearchParams(searchParams);
      if (next.page !== undefined) nsp.set('page', String(next.page + 1));
      if (next.pageSize !== undefined) nsp.set('pageSize', String(next.pageSize));
      setSearchParams(nsp, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const toggleIncludeInactive = useCallback(
    (next: boolean) => {
      const nsp = new URLSearchParams(searchParams);
      if (next) nsp.set('includeInactive', '1');
      else nsp.delete('includeInactive');
      setSearchParams(nsp, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const toggleOne = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const allPagedSelected =
    paged.length > 0 && paged.every((r) => selected.has(r.sweepstakes_code));
  const somePagedSelected = paged.some((r) => selected.has(r.sweepstakes_code));

  const toggleAllPaged = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPagedSelected) paged.forEach((r) => next.delete(r.sweepstakes_code));
      else paged.forEach((r) => next.add(r.sweepstakes_code));
      return next;
    });
  };

  const handleCreated = (row: AdminSweepstakesRow) => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'sweepstakes', 'list'] });
    setSnackbar({ open: true, msg: `Created ${row.sweepstakes_code}`, sev: 'success' });
    navigate(paths.admin.globalCatalog.sweepstakes(row.sweepstakes_code));
  };

  const showEmpty = !isLoading && rows.length === 0;

  return (
    <Box sx={{ maxWidth: 1400 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: colors.textTertiary,
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              mb: 0.5,
            }}
          >
            Admin · Global Catalog
          </Typography>
          <Typography sx={{ ...typo.pageTitle, mb: 0.5 }}>Sweepstakes</Typography>
          <Typography sx={{ ...typo.pageSubtitle }}>
            Platform sweepstake templates — entry economics, scheduling, prize pool config.
          </Typography>
        </Box>
        <WriteButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={primaryButtonSx}
        >
          Create sweepstake
        </WriteButton>
      </Box>

      {showEmpty ? (
        <EmptyState onCreate={() => setCreateOpen(true)} />
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 5,
              mb: 2,
              p: 2,
              borderRadius: '12px',
              border: `1px solid ${colors.borderLight}`,
              backgroundColor: colors.bgWhite,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <TextField
              size="small"
              placeholder="Search code or name… (press /)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputRef={searchRef}
              sx={{ minWidth: 260, flex: '1 1 260px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: colors.textTertiary }} />
                  </InputAdornment>
                ),
              }}
            />

            <ToggleButtonGroup
              size="small"
              value={statusFilter}
              onChange={(_, v) => setStatusFilter(v as AdminSweepstakesRow['status'][])}
              aria-label="Status filter"
            >
              {STATUS_FILTER_OPTIONS.map((s) => {
                const cp = statusChipProps(s);
                return (
                  <ToggleButton
                    key={s}
                    value={s}
                    sx={{
                      textTransform: 'none',
                      fontSize: 12,
                      fontWeight: 600,
                      px: 1.5,
                      '&.Mui-selected': { backgroundColor: cp.bg, color: cp.color },
                    }}
                  >
                    {cp.label}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={includeInactive}
                  onChange={(e) => toggleIncludeInactive(e.target.checked)}
                />
              }
              label={
                <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                  Show inactive
                </Typography>
              }
              sx={{ m: 0 }}
            />

            <Divider orientation="vertical" flexItem sx={{ borderColor: colors.borderLight }} />

            <ToggleButtonGroup
              size="small"
              exclusive
              value={timeChip}
              onChange={(_, v) => v && setTimeChip(v as TimeChip)}
            >
              {TIME_CHIPS.map((t) => (
                <ToggleButton
                  key={t.key}
                  value={t.key}
                  sx={{
                    textTransform: 'none',
                    fontSize: 12,
                    fontWeight: 600,
                    px: 1.5,
                  }}
                >
                  {t.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Paper>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: colors.adminAccent }} />
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: '12px',
                border: `1px solid ${colors.borderLight}`,
                overflow: 'hidden',
              }}
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...tableHeadCellSx, width: 44, px: 1 }}>
                        <Checkbox
                          size="small"
                          checked={allPagedSelected}
                          indeterminate={somePagedSelected && !allPagedSelected}
                          onChange={toggleAllPaged}
                          sx={{ p: 0 }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, width: 220 }}>Code</TableCell>
                      <TableCell sx={tableHeadCellSx}>Name</TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, width: 120 }}>Start</TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, width: 120 }}>End</TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, width: 140 }}>Status</TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, width: 140 }}>Updated</TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, width: 56, textAlign: 'center' }}>
                        By
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 8, textAlign: 'center' }}>
                          <Typography sx={{ ...typo.secondaryText }}>
                            No sweepstakes match the current filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paged.map((r) => {
                        const cp = statusChipProps(r.status);
                        const isSel = selected.has(r.sweepstakes_code);
                        const isInactive = !r.active;
                        return (
                          <TableRow
                            key={r.sweepstakes_code}
                            hover
                            sx={{
                              cursor: 'pointer',
                              opacity: isInactive ? 0.6 : 1,
                              transition: 'opacity 0.15s ease',
                              backgroundColor: isSel ? colors.adminAccentSubtle : undefined,
                              '&:hover': {
                                opacity: 1,
                                backgroundColor: isSel
                                  ? colors.adminAccentSubtle
                                  : colors.bgSubtle,
                              },
                            }}
                            onClick={() =>
                              navigate(
                                paths.admin.globalCatalog.sweepstakes(r.sweepstakes_code),
                              )
                            }
                          >
                            <TableCell
                              sx={{ ...tableCellSx, px: 1 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleOne(r.sweepstakes_code);
                              }}
                            >
                              <Checkbox size="small" checked={isSel} sx={{ p: 0 }} />
                            </TableCell>
                            <TableCell sx={{ ...tableCellSx, ...typo.mono, color: colors.textPrimary }}>
                              {r.sweepstakes_code}
                            </TableCell>
                            <TableCell sx={tableCellSx}>
                              {r.sweepstakes_name ?? '—'}
                              {isInactive && (
                                <Typography
                                  sx={{ fontSize: 11, color: colors.textTertiary, mt: 0.25 }}
                                >
                                  Deactivated on {formatRelative(r.updated_at)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ ...tableCellSx, color: colors.textSecondary }}>
                              {formatDate(r.start_date)}
                            </TableCell>
                            <TableCell sx={{ ...tableCellSx, color: colors.textSecondary }}>
                              {formatDate(r.end_date)}
                            </TableCell>
                            <TableCell sx={tableCellSx}>
                              <Chip
                                size="small"
                                label={isInactive ? 'Inactive' : cp.label}
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  backgroundColor: isInactive
                                    ? statusChipColors.inactive.bg
                                    : cp.bg,
                                  color: isInactive
                                    ? statusChipColors.inactive.color
                                    : cp.color,
                                  borderRadius: '6px',
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ ...tableCellSx, color: colors.textSecondary }}>
                              {formatRelative(r.updated_at)}
                            </TableCell>
                            <TableCell sx={{ ...tableCellSx, textAlign: 'center' }}>
                              <Tooltip title={r.updated_by || 'Unknown'}>
                                <Avatar
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    backgroundColor: colors.adminAccent,
                                    color: colors.bgWhite,
                                    mx: 'auto',
                                  }}
                                >
                                  {initialsFor(r.updated_by)}
                                </Avatar>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(_, p) => updateSearchParam({ page: p })}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[25, 50, 100, 200]}
                onRowsPerPageChange={(e) => {
                  updateSearchParam({ page: 0, pageSize: Number(e.target.value) });
                }}
              />
            </Paper>
          )}

          {selected.size > 0 && (
            <Paper
              elevation={4}
              sx={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                height: 64,
                zIndex: 10,
                backgroundColor: colors.adminAccent,
                color: colors.bgWhite,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                borderRadius: 0,
              }}
              role="toolbar"
              aria-label="Bulk actions"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                  {selected.size} selected
                </Typography>
                <Button
                  size="small"
                  startIcon={<CloseIcon />}
                  onClick={() => setSelected(new Set())}
                  sx={{
                    textTransform: 'none',
                    color: colors.bgWhite,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  Clear (Esc)
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip
                  title={
                    isSuperAdmin
                      ? 'Publish selected sweepstakes (P)'
                      : 'Publish requires super-admin role'
                  }
                >
                  <span>
                    <WriteButton
                      variant="contained"
                      disabled={!isSuperAdmin}
                      onClick={() => setPublishOpen(true)}
                      sx={{
                        backgroundColor: colors.bgWhite,
                        color: colors.adminAccent,
                        textTransform: 'none',
                        fontWeight: 700,
                        '&:hover': { backgroundColor: colors.adminAccentSubtle },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(255,255,255,0.35)',
                          color: 'rgba(255,255,255,0.55)',
                        },
                      }}
                    >
                      Publish selected
                    </WriteButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    selected.size === 1
                      ? 'Deactivate selected sweepstake'
                      : 'Select exactly 1 sweepstake to deactivate (bulk in a later round)'
                  }
                >
                  <span>
                    <WriteButton
                      variant="outlined"
                      disabled={selected.size !== 1}
                      onClick={() => {
                        if (selected.size !== 1) return;
                        const onlyCode = Array.from(selected)[0];
                        const row = rows.find((r) => r.sweepstakes_code === onlyCode);
                        if (row) setDeactivateFor(row);
                      }}
                      sx={{
                        textTransform: 'none',
                        borderColor: 'rgba(255,255,255,0.4)',
                        color: colors.bgWhite,
                        '&.Mui-disabled': {
                          borderColor: 'rgba(255,255,255,0.2)',
                          color: 'rgba(255,255,255,0.4)',
                        },
                      }}
                    >
                      Deactivate
                    </WriteButton>
                  </span>
                </Tooltip>
              </Box>
            </Paper>
          )}
        </>
      )}

      <CreateSweepstakesDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      <PublishConfirmationDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        entity="sweepstakes"
        entities={Array.from(selected)
          .map((code) => rows.find((r) => r.sweepstakes_code === code))
          .filter((r): r is AdminSweepstakesRow => !!r)
          .map<PublishEntity>((r) => toPublishEntityFromSweepstakes(r))}
        onCompleted={(res) => {
          setPublishOpen(false);
          if (res && res.batch_id) {
            setSnackbar({
              open: true,
              msg: `Publish batch completed`,
              sev: 'success',
            });
            queryClient.invalidateQueries({ queryKey: ['admin', 'sweepstakes', 'list'] });
            selected.forEach((code) => {
              queryClient.invalidateQueries({ queryKey: ['admin', 'sweepstakes', code] });
            });
            setSelected(new Set());
          }
        }}
      />

      {deactivateFor && (
        <DependencyImpactDialog
          open={!!deactivateFor}
          onClose={() => setDeactivateFor(null)}
          kind={{
            entity: 'sweepstakes',
            sweepstakesCode: deactivateFor.sweepstakes_code,
            baseVersion: deactivateFor.current_version,
          }}
          onSuccess={(row) => {
            setDeactivateFor(null);
            setSelected(new Set());
            setSnackbar({
              open: true,
              msg: `Deactivated ${row.code}`,
              sev: 'success',
            });
            queryClient.invalidateQueries({ queryKey: ['admin', 'sweepstakes', 'list'] });
            queryClient.invalidateQueries({
              queryKey: ['admin', 'sweepstakes', row.code],
            });
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.sev}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SweepstakesList;
